import { useEffect } from "react";
import useAdjustViewport from "./utils/useAdjustViewport.js";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import Privacy from "./components/Privacy.js";
import Terms from "./components/Terms.js";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getPageRoute } from "src/services";
import {
  PreventBackButton,
  BackNavigationPosition,
} from "src/utils/NavigationUtils.js";
import { NavigationManager } from "src/utils/NavigationManager.js";
import { TopNavbarProvider } from "./utils/TopBannerContext.js";
import { AuthProvider } from "./utils/AuthContext.js";
import Home from "./components/Home.js";
import Dashboard from "./components/Dashboard.js";
import SignIn from "./components/SignIn.js";
import LessonPlan from "./components/LessonPlan.js";
import CommunityPage from "./components/CommunityPage.js";
import ResourcesPage from "./components/ResourcesPage.js";
import WaitlistPage from "./components/WaitlistPage.js";
import FounderNote from "./components/FounderNote.js";
import ReportPage from "./components/ReportPage.js";

function App() {
  /*
    Refetch on window focus or reconnect
    keep previous data until cacheTime.
  */
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Infinity,
        cacheTime: 1000 * 60 * 10, // 10 minutes
        keepPreviousData: true,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        onError: (error) => {
          console.error("Query error:", error);
        },
      },
    },
  });

  useAdjustViewport();

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <TopNavbarProvider>
            <PreventBackButton />
            <NavigationManager />
            <BackNavigationPosition />
            <Routes>
              {/* --- 🍎 MARKETING SITE ROUTES --- */}
              <Route path={getPageRoute("home", "HOME")} element={<Home />} />
              <Route
                path={getPageRoute("waitlist", "WAITLIST")}
                element={<WaitlistPage />}
              />
              <Route
                path={getPageRoute("home", "FOUNDER_NOTE")}
                element={<FounderNote />}
              />
              <Route
                path={getPageRoute("home", "REPORT")}
                element={<ReportPage />}
              />

              {/* --- 🔐 AUTH + APP ROUTES --- */}
              <Route
                path={getPageRoute("auth", "SIGNIN")}
                element={<SignIn />}
              />
              <Route
                path={getPageRoute("maestro", "DASHBOARD")}
                element={<Dashboard />}
              />
              <Route
                path={getPageRoute("maestro", "DASHBOARD_LEGACY")}
                element={<Dashboard />}
              />
              <Route
                path={getPageRoute("maestro", "LESSON_PLAN")}
                element={<LessonPlan />}
              />
              <Route
                path={getPageRoute("maestro", "COMMUNITY")}
                element={<CommunityPage />}
              />
              <Route
                path={getPageRoute("maestro", "RESOURCES")}
                element={<ResourcesPage />}
              />

              {/* --- 📄 TERMS & PRIVACY ROUTES --- */}
              <Route
                path={getPageRoute("privacy", "PRIVACY")}
                element={<Privacy />}
              />
              <Route path={getPageRoute("terms", "TERMS")} element={<Terms />} />

              {/* Catch-all redirect to home */}
              <Route
                path="*"
                element={<Navigate to={getPageRoute("home", "HOME")} replace />}
              />
            </Routes>
          </TopNavbarProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
